import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Event as EventIcon,
  Place as PlaceIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import { useMyBookings } from '../../hooks/account/useMyBookings';
import { useToast } from '../../context/ToastContext';
import { BOOKING_STATUS_COLOR, formatSlot } from '../../constants/scheduling';
import { BookingCardSkeleton, Loading, StackSkeleton } from '../../components/layout/Skeletons';

/**
 * A professional's diary.
 *
 * Requests come first because they are the only thing that needs an answer;
 * everything else is there to be read.
 */
function MySchedule() {
  const toast = useToast();
  const { bookings, loading, error, act } = useMyBookings('/me/schedule');
  const [declining, setDeclining] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (booking, action, body = null, success = 'C’est enregistré.') => {
    setBusy(true);
    const message = await act(booking.uuid, action, body);
    setBusy(false);

    if (message) {
      toast.error(message);
      return false;
    }

    toast.success(success);

    return true;
  };

  const confirmDecline = async () => {
    if (!reason.trim()) return;

    const done = await run(
      declining,
      'decline',
      { reason: reason.trim() },
      'Demande refusée. L’habitant a été prévenu.'
    );

    if (done) {
      setDeclining(null);
      setReason('');
    }
  };

  const pending = bookings.filter((booking) => 'REQUESTED' === booking.status);
  const rest = bookings.filter((booking) => 'REQUESTED' !== booking.status);

  const card = (booking) => {
    const needsAnswer = 'REQUESTED' === booking.status;

    return (
    <Paper
      key={booking.uuid}
      variant="outlined"
      sx={{
        p: { xs: 2.5, md: 3 },
        // A request waiting on the professional is the only thing here that is
        // work; it should not look like the archive underneath it.
        borderColor: needsAnswer ? 'primary.main' : 'divider',
        borderWidth: needsAnswer ? 2 : 1,
        bgcolor: needsAnswer ? 'warning.light' : 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={booking.status_label}
          color={BOOKING_STATUS_COLOR[booking.status] || 'default'}
        />
      </Box>

      <Typography variant="h4" component="h3">
        {booking.subject}
      </Typography>

      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {booking.preferred_at
              ? formatSlot(booking.preferred_at)
              : 'Aucun moment précis demandé'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">{booking.customer?.name}</Typography>
        </Box>

        {booking.location && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlaceIcon fontSize="small" color="action" />
            <Typography variant="body2">{booking.location}</Typography>
          </Box>
        )}
      </Stack>

      {booking.note && (
        <Box
          sx={{
            mt: 2,
            pl: 2,
            borderLeft: '3px solid',
            borderColor: 'surface.borderStrong',
          }}
        >
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontStyle: 'italic' }}>
            {booking.note}
          </Typography>
        </Box>
      )}

      {booking.decision_reason && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {booking.decision_reason}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        {'REQUESTED' === booking.status && (
          <>
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              disabled={busy}
              onClick={() => run(booking, 'confirm', null, 'Rendez-vous confirmé.')}
            >
              Confirmer
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              disabled={busy}
              onClick={() => setDeclining(booking)}
            >
              Refuser
            </Button>
          </>
        )}

        {'CONFIRMED' === booking.status && (
          <>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => run(booking, 'complete', null, 'Intervention marquée comme terminée.')}
            >
              Marquer comme fait
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={busy}
              onClick={() => run(booking, 'cancel', { reason: null }, 'Rendez-vous annulé.')}
            >
              Annuler
            </Button>
          </>
        )}
      </Box>
    </Paper>
    );
  };

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          title="Mon agenda"
          lede="Le créneau reste retenu tant que vous n’avez pas répondu : une demande laissée en attente bloque une place chez vous."
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Loading label="Chargement de votre agenda">
            <Skeleton variant="text" width={200} height={38} sx={{ mb: 2 }} />
            <StackSkeleton count={2}>{BookingCardSkeleton}</StackSkeleton>
          </Loading>
        ) : (
          <Stack spacing={4}>
            <Box>
              <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
                À répondre {pending.length > 0 ? `(${pending.length})` : ''}
              </Typography>

              {0 === pending.length ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed' }}>
                  <Typography variant="body1">Aucune demande en attente.</Typography>
                </Paper>
              ) : (
                <Stack spacing={2.25}>{pending.map(card)}</Stack>
              )}
            </Box>

            {rest.length > 0 && (
              <Box>
                <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
                  Le reste
                </Typography>
                <Stack spacing={2.25}>{rest.map(card)}</Stack>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      <Dialog open={Boolean(declining)} onClose={() => setDeclining(null)} fullWidth maxWidth="sm">
        <DialogTitle>Refuser cette demande</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            L’habitant lira votre motif, et le créneau repart aussitôt. Un refus sans
            explication ressemble à une panne du site.
          </DialogContentText>
          <TextField
            label="Motif"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclining(null)} disabled={busy} variant="outlined">
            Revenir
          </Button>
          <Button color="error" variant="contained" onClick={confirmDecline} disabled={busy || !reason.trim()}>
            Refuser
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

export default MySchedule;
